import { z } from 'zod';
import { AREA_CODE_PATTERN, ROLE_CODES, type RoleCode } from './constants';
import type { RoleGrant } from './session';

// ------------------------------------------------------------------ users (AUTH-07)

export const userListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  role: z.enum(ROLE_CODES).optional(),
  area: z.string().max(40).optional(),
  active: z.enum(['1', '0']).optional(),
});
export type UserListQuery = z.infer<typeof userListQuerySchema>;

export interface UserListItem {
  id: string;
  username: string;
  name: string;
  email: string | null;
  isActive: boolean;
  grants: RoleGrant[];
  lastLoginAt: string | null;
  createdAt: string;
}

export const userUpdateSchema = z.object({ isActive: z.boolean() });

// ------------------------------------------------------------------ SSO role mapping (SET-07)

export interface SsoMappingView {
  ssoRoleCode: string;
  role: RoleCode;
  area: string | null;
  /** Users holding this SSO role at their last login. */
  userCount: number;
}

export const ssoMappingSchema = z.object({
  ssoRoleCode: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      /^[a-z0-9_.-]{2,40}$/,
      'Kode role SSO: huruf kecil, angka, titik, garis bawah atau strip (2–40 karakter)',
    ),
  role: z.enum(ROLE_CODES),
  area: z.string().regex(AREA_CODE_PATTERN, 'Kode wilayah tidak valid').nullable(),
});
export type SsoMappingInput = z.infer<typeof ssoMappingSchema>;

// ------------------------------------------------------------------ audit log (AUD-01/02)

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Masuk',
  'auth.logout': 'Keluar',
  'auth.denied': 'Login ditolak',
  'permission.denied': 'Akses ditolak',
  'area.boundary.update': 'Ubah batas wilayah',
  'area.create': 'Tambah wilayah',
  'area.update': 'Ubah wilayah',
  'area.delete': 'Hapus wilayah',
  'asset.create': 'Tambah aset',
  'asset.update': 'Ubah aset',
  'asset.move': 'Pindahkan aset',
  'asset.delete': 'Hapus aset',
  'asset.restore': 'Pulihkan aset',
  'port.update': 'Ubah port',
  'port.link': 'Hubungkan port',
  'port.unlink': 'Putuskan port',
  'route.create': 'Buat jalur',
  'route.update': 'Ubah jalur',
  'route.path': 'Ubah bentuk jalur',
  'route.delete': 'Hapus jalur',
  'route.restore': 'Pulihkan jalur',
  'route.poles': 'Tiang otomatis',
  'data.import': 'Import data',
  'data.export': 'Export data',
  'monitoring.configure': 'Atur monitoring',
  'alert.ack': 'Akui insiden',
  'alert.resolve': 'Tutup insiden',
  'user.update': 'Ubah status pengguna',
  'sso_mapping.create': 'Tambah pemetaan role',
  'sso_mapping.update': 'Ubah pemetaan role',
  'sso_mapping.delete': 'Hapus pemetaan role',
  'role.permissions': 'Ubah hak akses peran',
  'masterdata.update': 'Ubah master data',
  'settings.update': 'Ubah pengaturan',
  'pricing.update': 'Ubah harga default',
  'mcp.connect': 'Hubungkan AI agent',
  'mcp.revoke': 'Cabut AI agent',
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format tanggal YYYY-MM-DD')
  .refine(
    (v) =>
      !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().startsWith(v),
    'Tanggal tidak valid',
  );

export const auditQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  userId: z.string().uuid().optional(),
  action: z.string().max(60).optional(),
  entity: z.string().max(40).optional(),
  /** Inclusive dates in Asia/Jakarta. */
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export interface AuditEntry {
  id: string;
  at: string;
  action: string;
  entity: string | null;
  entityId: string | null;
  /** Code/name of the asset or route when the entity still exists. */
  entityLabel: string | null;
  userId: string | null;
  userName: string | null;
  ip: string | null;
  userAgent: string | null;
  diff: unknown;
}
